import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';

interface NavLogoGlowColorSelectorProps {
  navLogoGlowColor?: string;
  globalPrimaryColor: string;
  globalSecondaryColor: string;
  onNavLogoGlowColorChange: (color: string) => void;
}

/**
 * NavLogoGlowColorSelector - Color picker for nav logo glow effect
 * Similar to CategoryUnderlineColorSelector for basic color selection
 */
const NavLogoGlowColorSelector: React.FC<NavLogoGlowColorSelectorProps> = ({
  navLogoGlowColor,
  globalPrimaryColor,
  globalSecondaryColor,
  onNavLogoGlowColorChange
}) => {
  // Color presets similar to CategoryUnderlineColorSelector
  const colorPresets = [
    { name: 'Primary', color: globalPrimaryColor },
    { name: 'Secondary', color: globalSecondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Blue', color: '#3f92ff' },
    { name: 'Green', color: '#3cc35c' },
    { name: 'Gold', color: '#c5b068' },
    { name: 'Sand', color: '#FFFBF2' }
  ];

  const handleColorPresetClick = (color: string) => {
    onNavLogoGlowColorChange(color);
  };

  const handleResetToGlobal = () => {
    onNavLogoGlowColorChange(globalPrimaryColor);
  };

  const currentNavLogoGlowColor = navLogoGlowColor || globalPrimaryColor;
  const isUsingGlobalColors = currentNavLogoGlowColor === globalPrimaryColor;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Glow Colour</Label>
        <div 
          className="w-20 h-6 rounded border border-gray-300"
          style={{ backgroundColor: currentNavLogoGlowColor }}
        />
      </div>
      
      <div className="space-y-4">
        {/* Single Color Control */}
        <div className="space-y-2">
          <Label className="text-xs text-gray-600">Colour</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
              >
                <div 
                  className="w-4 h-4 rounded border border-gray-300"
                  style={{ backgroundColor: currentNavLogoGlowColor }}
                />
                {currentNavLogoGlowColor}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="space-y-3">
                <Label className="text-sm font-medium">Pick glow colour</Label>
                <Input
                  type="color"
                  value={currentNavLogoGlowColor}
                  onChange={(e) => onNavLogoGlowColorChange(e.target.value)}
                  className="w-full h-10"
                />
                <Input
                  type="text"
                  value={currentNavLogoGlowColor}
                  onChange={(e) => onNavLogoGlowColorChange(e.target.value)}
                  placeholder="#ffffff"
                  className="font-mono text-sm"
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Reset Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleResetToGlobal}
          disabled={isUsingGlobalColors}
          className="w-full"
        >
          ↺ Reset to Global
        </Button>

        {/* Color Presets */}
        <div className="space-y-2">
          <Label className="text-xs text-gray-600">Quick Presets</Label>
          <div className="grid grid-cols-2 gap-2">
            {colorPresets.map((preset) => (
              <Button
                key={preset.name}
                variant="outline"
                size="sm"
                onClick={() => handleColorPresetClick(preset.color)}
                className="justify-start gap-2 h-auto p-2"
              >
                <div 
                  className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
                  style={{ backgroundColor: preset.color }}
                />
                <span className="text-xs text-left">{preset.name}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="text-xs text-gray-500">
        Configure the colour for the logo text glow effect
      </div>
    </div>
  );
};

export default NavLogoGlowColorSelector;